import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AppContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or get user ID from localStorage
const getUserId = () => {
  let userId = localStorage.getItem('behat_user_id');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('behat_user_id', userId);
  }
  return userId;
};

export const AppProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], id: '', user_id: '' });
  const [wishlist, setWishlist] = useState({ product_ids: [], id: '', user_id: '' });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [craftTypes, setCraftTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const userId = getUserId();

  // Fetch cart
  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${userId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  // Fetch wishlist
  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/${userId}`);
      setWishlist(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  };

  // Fetch products
  const fetchProducts = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.craft_type) params.append('craft_type', filters.craft_type);
      if (filters.featured !== undefined) params.append('featured', filters.featured);
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const [productCats, craftCats] = await Promise.all([
        axios.get(`${API}/categories?type=product`),
        axios.get(`${API}/categories?type=craft`)
      ]);
      setCategories(productCats.data);
      setCraftTypes(craftCats.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Add to cart
  const addToCart = async (productId, quantity = 1) => {
    try {
      await axios.post(`${API}/cart/${userId}/add`, {
        product_id: productId,
        quantity: quantity
      });
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  // Update cart item
  const updateCartItem = async (productId, quantity) => {
    try {
      await axios.put(`${API}/cart/${userId}/update`, {
        product_id: productId,
        quantity: quantity
      });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  // Remove from cart
  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${userId}/remove/${productId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/${userId}/clear`);
      await fetchCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Add to wishlist
  const addToWishlist = async (productId) => {
    try {
      await axios.post(`${API}/wishlist/${userId}/add/${productId}`);
      await fetchWishlist();
      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return false;
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${userId}/remove/${productId}`);
      await fetchWishlist();
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.product_ids.includes(productId);
  };

  // Get cart item count
  const getCartCount = () => {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  useEffect(() => {
    fetchCart();
    fetchWishlist();
    fetchCategories();
    fetchProducts();
  }, []);

  const value = {
    cart,
    wishlist,
    products,
    categories,
    craftTypes,
    loading,
    userId,
    fetchProducts,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getCartCount,
    fetchCart,
    fetchWishlist
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
